/** One filing: where it stands, then six tabs (journey 4).
 *
 * The long page this replaces answered five different questions in one scroll. They are tabs now,
 * in the order the work runs: read the **Disclosures**, tick the **Checklist**, clear the
 * **Findings**, compare with last year (**Year-on-year bridge**), then **Issue**. The things that
 * must be true whichever tab you are on do not move: the regime's in-force caveat, the verdict in
 * its own words, and the four facts that decide what to do next.
 *
 * Four rules the header keeps:
 *
 * - **"Checks passed" is not approval** (F07). The verdict block is the original's: the headline
 *   names what was done, the sentence under it says what was not checked and that a person decides.
 *   The block is not green for `checks_passed`; only `approved` and `issued`, which have a person
 *   behind them, take the positive tone.
 * - **A filing is addressed by its obligation id** (`?filing=`, ADR-0029), never by regime and
 *   dates, and the tab is `?tab=`. A tab nothing matches opens Disclosures.
 * - **A read that failed says so.** The report and the issued-filings count each carry their own
 *   failure; neither is shown as a zero or as a short list.
 * - **Issuing is not gated on the verdict** (ADR-0020). Nothing on this screen disables the Issue
 *   tab because checks have not passed; it says whose judgement that is.
 */

import { ArrowLeft } from "@phosphor-icons/react";
import { Link, useNavigate } from "@tanstack/react-router";

import { useMe } from "../api/auth";
import { useFilingChecks } from "../api/filingChecks";
import type { Obligation } from "../api/queries";
import { useArtifacts, useReport } from "../api/queries";
import { KpiTile } from "../components/KpiTile";
import { Loaded } from "../components/Loaded";
import { PageHeader } from "../components/PageHeader";
import { Panel } from "../components/Panel";
import { quantity, shortDate } from "../lib/format";
import { PASSING, VERDICT_HEADLINE } from "./verdictWords";
import { Movement } from "./ReportsBridge";
import { ChecksTab } from "./ReportsChecks";
import { FilingJourney } from "./ReportsJourney";
import { ReportsIssueTab } from "./ReportsIssueTab";
import { VerdictGlyph } from "./ReportsFindingRow";
import { FiguresInRegister } from "./ReportsLines";
import { ChecklistTab, DisclosuresTab, FindingsTab } from "./ReportsTabs";
import { RecordPanel, RecordTabs } from "./RecordTabs";
import { SubmissionTab } from "./ReportsSubmission";
import own from "./Reports.module.css";
import { amount, cls } from "./reportsFormat";
import { countsLine } from "./filingChecksModel";
import { FILING_TABS, filingTabOf, isOwed } from "./reportsModel";
import { keepSearch } from "./search";
import type { ScreenSearch } from "./search";
import styles from "./Screen.module.css";
import type { Screen } from "./useScreen";

export function ReportsDetail({ screen, active }: { screen: Screen; active: Obligation }) {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const tab = filingTabOf(screen.tab);
  const goTab = (key: string) => {
    void navigate({
      to: ".",
      // A tab change is a push, so Back returns to the tab the reader left. Every long view names
      // itself in the URL, Disclosures included: an absent `tab` is the filing's page, not a view.
      search: (prev: Record<string, unknown>) => ({ ...prev, tab: key }),
    });
  };

  const reportRead = useReport(active.obligation_id, screen.method);
  const report = reportRead.data;
  const artifactsRead = useArtifacts(active.obligation_id);
  // **One read feeds every count on this view and on the filing's page** (design review 2026-11-03).
  // The verdict block, the tile and the Findings list used to count the report's own rules (`7
  // checks run`) while the page counted all of the filing's checks (`10 of 14 passed`), so a filing
  // with three blocking checks said "0 blocking" one press away from the page that listed them.
  const checksRead = useFilingChecks(active.obligation_id, screen.method);
  const checks = checksRead.data;

  const owed = isOwed(active);
  const name = `${active.regime_key.replace(/_/g, " ")}, ${active.legal_entity_name}`;

  return (
    <>
      <PageHeader
        title={name}
        subtitle={`Year ended ${shortDate(active.financial_year_end)} · ${screen.method}-based Scope 2 · rules version ${active.regime_version}`}
        actions={
          <>
            <Link
              to="/reports"
              search={(prev: Partial<ScreenSearch>) => ({ ...keepSearch(prev), filing: active.obligation_id })}
              className={styles.fixLink}
              data-testid="filing-page-link"
            >
              <ArrowLeft size={13} aria-hidden="true" /> Back to the filing
            </Link>{" "}
            <Link
              to="/reports"
              search={(prev: Partial<ScreenSearch>) => ({ ...keepSearch(prev), filing: undefined })}
              className={styles.fixLink}
              data-testid="all-filings"
            >
              All filings
            </Link>
          </>
        }
      />

      {/* **REP-01: whether this regime is law, above the verdict.**
          SB 253's 10 November 2026 filing date was proposed until 12 October 2026 (C-03: OAL
          approved the regulation), and SB 261 is enjoined (C-07). The paragraphs in the
          disclosures are real, the citations resolve and the figures are right, and without this
          line the whole screen reads as an operative mandate. Above the verdict rather than beside
          the citations, because it qualifies every line at once; and above the tabs, so it is on
          every one of them. */}
      {report?.in_force_caveat ? (
        <p className={cls(own.notice, own.noticeWarn, own.wide)} data-testid="in-force">
          <span className={cls(own.noticeTitle)}>{report.in_force_label}</span>
          {report.in_force_caveat}
        </p>
      ) : null}

      <div className={own.head}>
        {/* **A failed report read is not "no regime determined"** (EXP-29). That sentence says this
            company has nothing to file, which is the one claim this screen must never make by
            accident; `Loaded` says the read failed, with a way to try again. */}
        <Loaded
          query={reportRead}
          of="This filing"
          empty="No regime determined for this workspace yet."
        >
          {(filing) => (
            /* **Four states, and the top one is not approval** (F07).
               This said "Every check this regime defines has passed" over a regime with **no rules
               loaded**, because the flag behind it was `not any(blocking)` and `any([])` is false.
               The headline now names the state, and the sentence under it says why - including
               what was not checked, which is the part a reader needs and the old wording could
               never carry. */
            <div
              className={cls(
                own.verdict,
                filing.verdict === "checks_passed"
                  ? own.verdictInfo
                  : PASSING.has(filing.verdict)
                    ? own.verdictYes
                    : own.verdictNo,
              )}
              data-testid="verdict"
            >
              <span className={cls(own.verdictHead)}>
                <VerdictGlyph verdict={filing.verdict} />
                {VERDICT_HEADLINE[filing.verdict] ?? filing.verdict}
              </span>
              <span className={cls(own.verdictSub)}>
                {checks ? countsLine(checks) : "Counting the checks"}
              </span>
              <span className={cls(own.verdictWhy)}>{filing.verdict_reason}</span>
            </div>
          )}
        </Loaded>

        {report ? (
          <FilingJourney active={active} report={report} method={screen.method} onGoTab={goTab} />
        ) : null}

        {report ? (
          <section className={own.facts} aria-label="This filing at a glance" data-testid="filing-facts">
            <KpiTile
              kpi="filing-due"
              label="Due"
              textual
              value={owed && active.due_date ? shortDate(active.due_date) : "No date shown"}
              note={active.due_date_basis || undefined}
            />
            <KpiTile
              kpi="filing-total"
              label="Stated under the regulation"
              value={amount(report.stated_total_tonnes, "tCO2e")}
              unit="tCO₂e"
              note="Guidance lines are printed apart and left out of this total"
              footer={
                <FiguresInRegister report={report} testId="filing-figures-link">
                  Show the figures behind it
                </FiguresInRegister>
              }
            />
            {/* The same words and the same read as the filing's page: "Checks, 7 of 9 passed". */}
            <KpiTile
              kpi="filing-blocking"
              label="Checks"
              textual={!checks}
              value={
                checks
                  ? `${quantity(String(checks.summary.passed), 0)} of ${quantity(String(checks.summary.total), 0)} passed`
                  : checksRead.error
                    ? "Not read"
                    : "Reading…"
              }
              note={
                checks
                  ? `${quantity(String(checks.counts.blocking ?? 0), 0)} blocking, ${quantity(String(checks.counts.advisory ?? 0), 0)} advisory`
                  : checksRead.error
                    ? "Their count could not be read; it is not zero"
                    : undefined
              }
              footer={
                <button type="button" className={styles.fixLink} onClick={() => goTab("findings")}>
                  Read the findings ›
                </button>
              }
            />
            <KpiTile
              kpi="filing-issued"
              label="Filings issued"
              textual={!artifactsRead.data}
              value={
                artifactsRead.data
                  ? quantity(String(artifactsRead.data.length), 0)
                  : artifactsRead.error
                    ? "Not read"
                    : "Reading…"
              }
              note={
                artifactsRead.error
                  ? "Their count could not be read; it is not zero"
                  : "Frozen, hashed and on the ledger"
              }
              footer={
                <button type="button" className={styles.fixLink} onClick={() => goTab("issue")}>
                  Open Issue ›
                </button>
              }
            />
          </section>
        ) : null}
      </div>

      <div className={own.tabsRow}>
        <RecordTabs
          tabs={FILING_TABS}
          tab={tab}
          onSelect={goTab}
          label="Filing views"
          saying={`Showing ${FILING_TABS.find((entry) => entry.key === tab)?.label ?? "Disclosures"} for ${name}`}
        />
      </div>

      <div className={own.tabBody}>
        <RecordPanel tab={tab}>
          <div className={own.stack}>
            {tab === "disclosures" ? (
              report ? (
                <DisclosuresTab
                  report={report}
                  active={active}
                  method={screen.method}
                  role={me?.role}
                />
              ) : (
                <Panel title="Disclosures" sub="What this regime requires to be stated" column="1 / 13">
                  <p className={own.fine} role="status">
                    {reportRead.error
                      ? "The disclosures come from this filing's report, which could not be read. Try again above."
                      : "Reading this filing's report…"}
                  </p>
                </Panel>
              )
            ) : null}
            {tab === "checks" ? <ChecksTab active={active} method={screen.method} /> : null}
            {tab === "checklist" ? <ChecklistTab report={report} active={active} method={screen.method} /> : null}
            {tab === "findings" ? <FindingsTab checks={checksRead} /> : null}
            {tab === "bridge" ? (
              <Movement
                obligationId={active.obligation_id}
                method={screen.method}
                entityName={active.legal_entity_name}
              />
            ) : null}
            {tab === "issue" ? <ReportsIssueTab screen={screen} active={active} role={me?.role} /> : null}
            {tab === "submission" ? (
              <SubmissionTab active={active} method={screen.method} role={me?.role} />
            ) : null}
          </div>
        </RecordPanel>
      </div>
    </>
  );
}
